import { format } from 'date-fns'
import { Pencil } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { parseDateOnly } from '../lib/formatters'
import { type Driver, DriverStatus } from '../models/driver'
import { DetailRow, DiffRow, PendingBanner, type PendingKind } from './PendingDetailRows'

function fmtDate(value?: string | null): string {
  if (!value) return ''
  const parsed = parseDateOnly(value)
  return parsed ? format(parsed, 'MM/dd/yyyy') : value
}

function fmtNum(value?: number | null): string {
  return value != null ? String(value) : ''
}

export function DriverDetailsDialog({
  driver,
  open,
  onOpenChange,
  onEdit,
}: {
  driver: Driver
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Shows an Edit button; omit it for a driver that can't be edited. */
  onEdit?: () => void
}) {
  const pending = driver.pendingChange
  const kind: PendingKind | null =
    driver.status === DriverStatus.PENDING_ADD
      ? 'add'
      : pending?.status === DriverStatus.PENDING_REMOVE
        ? 'remove'
        : pending
          ? 'change'
          : null
  // Only a PENDING_CHANGE carries new field values worth diffing against the live row.
  const change = kind === 'change' ? pending : null

  const title = [driver.firstName, driver.lastName].filter(Boolean).join(' ') || driver.email || 'Driver'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {kind && <PendingBanner kind={kind} />}
        <div className="grid grid-cols-2 gap-4">
          <DiffRow label="First name" oldValue={driver.firstName} newValue={change?.firstName} />
          <DiffRow label="Last name" oldValue={driver.lastName} newValue={change?.lastName} />
          <DetailRow label="Email" value={driver.email} />
          <DetailRow label="Mobile" value={driver.mobile} />
          <DiffRow label="License number" oldValue={driver.licenseNumber} newValue={change?.licenseNumber} />
          <DiffRow label="License state" oldValue={driver.licenseState} newValue={change?.licenseState} />
          <DiffRow
            label="Date of birth"
            oldValue={fmtDate(driver.dob)}
            newValue={change ? fmtDate(change.dob) : undefined}
          />
          <DiffRow
            label="Date of hire"
            oldValue={fmtDate(driver.dateOfHire)}
            newValue={change ? fmtDate(change.dateOfHire) : undefined}
          />
          <DetailRow label="Date on policy" value={fmtDate(driver.dateOnPolicy)} />
          <DiffRow
            label="Years of experience"
            oldValue={fmtNum(driver.yearsOfExperience)}
            newValue={change ? fmtNum(change.yearsOfExperience) : undefined}
          />
          <DiffRow
            label="Violations"
            oldValue={fmtNum(driver.violationsCount)}
            newValue={change ? fmtNum(change.violationsCount) : undefined}
          />
          <DiffRow
            label="At-fault accidents"
            oldValue={fmtNum(driver.accidentsCount)}
            newValue={change ? fmtNum(change.accidentsCount) : undefined}
          />
          <DiffRow
            label="Suspensions"
            oldValue={fmtNum(driver.suspensionsCount)}
            newValue={change ? fmtNum(change.suspensionsCount) : undefined}
          />
          <DetailRow label="CDL issue date" value={fmtDate(driver.cdlDate)} />
          <DetailRow label="Telematics ID" value={driver.telematicsId} />
          <DetailRow label="Status" value={driver.status} />
        </div>
        {onEdit && (
          <DialogFooter className="mt-0">
            <Button onClick={onEdit}>
              <Pencil />
              Edit driver
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
